import pino from 'pino';

type Level = Exclude<pino.Level, 'trace'>;
type LevelWithSilent = Exclude<pino.LevelWithSilent, 'trace'>;

type LogOptions = pino.LoggerOptions & {
  /**
   * Used to identify the application that is logging and is included in every log entry.
   *
   * Choose a name that uniquely identifies the application.
   * @example 'klohkhuis'
   * @default undefined
   */
  name: string;

  /**
   * The level from which log entries will be recorded.
   *
   * For example, if you set this to 'info', only log entries with a level of 'info' or higher will be recorded.
   * @default 'info'
   */
  level: LevelWithSilent;
};

type Message = string;
type Context = {
  message: Message;
  [key: string]: unknown;
};
type ContextError = Context & {
  err: Error;
};
type LogFn = (opts: Message | Context) => void;
type LogFnWithError = LogFn & ((opts: Error | ContextError) => void);

interface BaseLogger {
  debug: LogFn;
  info: LogFn;
  warn: LogFnWithError;
  error: LogFnWithError;
  fatal: LogFnWithError;
}

type LoggerExtras = {
  child: (bindings: Record<string, unknown>) => BaseLogger;
};

type Logger = Omit<
  pino.BaseLogger,
  'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal' | 'silent'
> &
  BaseLogger &
  LoggerExtras;

// https://getpino.io/#/docs/help?id=mapping-pino-log-levels-to-google-cloud-logging-stackdriver-serverity-levels
const PinoLevelToSeverityLookup = {
  debug: 'debug',
  info: 'info',
  warn: 'warning',
  error: 'error',
  fatal: 'critical',
};

function getSeverity(level: string): string {
  return (
    PinoLevelToSeverityLookup[level as Level] ?? PinoLevelToSeverityLookup.info
  );
}

const defaultPinoConf: pino.LoggerOptions = {
  messageKey: 'message',
  formatters: {
    level(label: string, number: number) {
      const severity = getSeverity(label);

      return {
        severity,
        level: number,
      };
    },
    log(args) {
      return args;
    },
  },
};

export function createLogger(options: LogOptions): Logger {
  if (global?.process?.env?.NODE_ENV === 'development') {
    defaultPinoConf.transport = {
      target: 'pino-pretty',
      options: {
        messageKey: 'message',
        translateTime: 'yyyy-mm-dd hh:MM:ss Z',
        ignore: 'pid,hostname,name,severity',
      },
    };
  }

  return pino({ ...options, ...defaultPinoConf });
}

export type { Logger, LevelWithSilent };
